import type { CSSProperties } from 'react';
import Typography from '@material-ui/core/Typography';
import RateReviewIcon from '@material-ui/icons/RateReview';
import { useKnowledgeBuilderColors } from '../../../theme/knowledgeBuilderColors';
import { useDefinitionInterview } from './DefinitionContext';
import type { SectionDotStatus } from './DefinitionContext';

export const SectionNav = () => {
  const colors = useKnowledgeBuilderColors();
  const {
    schema,
    activeSectionId,
    isReviewSelected,
    selectSection,
    selectReview,
    sectionStatus,
  } = useDefinitionInterview();

  const totalSteps = schema?.sections.length ?? 0;
  const completeSteps = (schema?.sections ?? []).filter(
    section => sectionStatus(section.id) === 'complete',
  ).length;
  const percent = totalSteps
    ? Math.round((completeSteps / totalSteps) * 100)
    : 0;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: 220,
        flex: '0 0 220px',
        borderRight: `1px solid ${colors.border.default}`,
        background: colors.bg.panel,
        padding: '12px 8px',
        gap: 2,
      }}
    >
      <Typography
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: colors.text.muted,
          padding: '4px 8px 8px',
        }}
      >
        Definition
      </Typography>
      {(schema?.sections ?? []).map(section => (
        <NavRow
          key={section.id}
          label={section.label}
          status={sectionStatus(section.id)}
          active={!isReviewSelected && activeSectionId === section.id}
          onClick={() => selectSection(section.id)}
        />
      ))}
      <div style={{ height: 8 }} />
      <button
        type="button"
        onClick={selectReview}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 8px',
          borderRadius: 6,
          border: 0,
          textAlign: 'left',
          cursor: 'pointer',
          background: isReviewSelected ? colors.bg.hover : 'transparent',
          color: isReviewSelected ? colors.text.onAccent : colors.text.primary,
          fontSize: 13,
        }}
      >
        <RateReviewIcon style={{ fontSize: 15 }} />
        Review
      </button>
      <div style={{ flex: 1 }} />
      <div style={{ padding: '8px' }}>
        <Typography
          style={{ fontSize: 11, color: colors.text.muted, marginBottom: 4 }}
        >
          Completion
        </Typography>
        <div
          style={{
            height: 6,
            borderRadius: 3,
            background: colors.bg.input,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${percent}%`,
              background: colors.accent.blue,
              transition: 'width 200ms ease',
            }}
          />
        </div>
        <Typography style={{ fontSize: 11, color: colors.text.muted, marginTop: 4 }}>
          {percent}% ({completeSteps}/
          {totalSteps})
        </Typography>
      </div>
    </div>
  );
};

function NavRow(options: {
  label: string;
  status: SectionDotStatus;
  active: boolean;
  onClick(): void;
}) {
  const colors = useKnowledgeBuilderColors();
  return (
    <button
      type="button"
      onClick={options.onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        borderRadius: 6,
        border: 0,
        textAlign: 'left',
        cursor: 'pointer',
        background: options.active ? colors.bg.hover : 'transparent',
        color: options.active ? colors.text.onAccent : colors.text.primary,
        fontSize: 13,
      }}
    >
      <Dot status={options.status} colors={colors} />
      {options.label}
    </button>
  );
}

function Dot(options: {
  status: SectionDotStatus;
  colors: ReturnType<typeof useKnowledgeBuilderColors>;
}) {
  const { status, colors } = options;
  if (status === 'complete') {
    return (
      <span
        style={{
          ...dotStyle,
          borderRadius: '50%',
          background: colors.accent.blue,
        }}
      />
    );
  }
  if (status === 'hasFindings') {
    return (
      <span style={{ ...dotStyle, color: colors.status.warningText }}>⚠</span>
    );
  }
  if (status === 'unresolved') {
    return (
      <span
        style={{
          ...dotStyle,
          borderRadius: '50%',
          background: colors.border.strong,
        }}
      />
    );
  }
  if (status === 'current') {
    return (
      <span
        style={{
          ...dotStyle,
          border: `1.5px solid ${colors.accent.blue}`,
          borderRadius: '50%',
        }}
      />
    );
  }
  return (
    <span
      style={{
        ...dotStyle,
        border: `1.5px solid ${colors.border.strong}`,
        borderRadius: '50%',
      }}
    />
  );
}

const dotStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 14,
  height: 14,
  fontSize: 11,
  lineHeight: '14px',
  flex: '0 0 14px',
};
